/**
 * Priority utilities for consistent priority handling across the app
 */

export type PriorityLevel = 'urgent' | 'high' | 'medium' | 'low' | 'none'

export interface PriorityOption {
  value: number
  label: string
  level: PriorityLevel
  description?: string
}

/**
 * Standard priority options used throughout the app
 */
export const PRIORITY_OPTIONS: Array<PriorityOption> = [
  { value: 0, label: 'None', level: 'none', description: 'No priority set' },
  { value: 1, label: 'Low', level: 'low', description: 'Nice to have' },
  { value: 2, label: 'Medium', level: 'medium', description: 'Should have' },
  { value: 3, label: 'High', level: 'high', description: 'Must have' },
  { value: 4, label: 'Urgent', level: 'urgent', description: 'Critical and time-sensitive' },
]

/**
 * Get priority level from numeric value
 * 
 * @param priority - Numeric priority value (0-4)
 * @returns Priority level string
 * 
 * @example
 * ```tsx
 * const level = getPriorityLevel(3) // 'high'
 * ```
 */
export function getPriorityLevel(priority: number): PriorityLevel {
  const option = PRIORITY_OPTIONS.find(opt => opt.value === priority)
  return option?.level ?? 'none'
}

/**
 * Get priority label from numeric value
 * 
 * @param priority - Numeric priority value (0-4)
 * @returns Priority label string
 * 
 * @example
 * ```tsx
 * const label = getPriorityLabel(3) // 'High'
 * ```
 */
export function getPriorityLabel(priority: number): string {
  const option = PRIORITY_OPTIONS.find(opt => opt.value === priority)
  return option?.label ?? 'None'
}

/**
 * Get priority description from numeric value
 * 
 * @param priority - Numeric priority value (0-4)
 * @returns Priority description string
 * 
 * @example
 * ```tsx
 * const description = getPriorityDescription(3) // 'Must have'
 * ```
 */
export function getPriorityDescription(priority: number): string {
  const option = PRIORITY_OPTIONS.find(opt => opt.value === priority)
  return option?.description ?? 'No priority set'
}

/**
 * Get priority option object from numeric value
 * 
 * @param priority - Numeric priority value (0-4)
 * @returns Complete priority option or null
 * 
 * @example
 * ```tsx
 * const option = getPriorityOption(3)
 * // { value: 3, label: 'High', level: 'high', description: 'Must have' }
 * ```
 */
export function getPriorityOption(priority: number): PriorityOption | null {
  return PRIORITY_OPTIONS.find(opt => opt.value === priority) ?? null
}

/**
 * Get CSS color classes for priority level
 * 
 * @param priority - Numeric priority value (0-4)
 * @returns Object with text and background color classes
 * 
 * Follows the app-wide semantic swatch convention: hued levels pair a light
 * step with a dark-mode counterpart, while the neutral level uses theme tokens.
 *
 * @example
 * ```tsx
 * const colors = getPriorityColors(3)
 * // { text: 'text-orange-800 dark:text-orange-200', ... }
 * ```
 */
export function getPriorityColors(priority: number): {
  text: string
  bg: string
  border: string
} {
  const level = getPriorityLevel(priority)

  switch (level) {
    case 'urgent':
      return {
        text: 'text-red-800 dark:text-red-200',
        bg: 'bg-red-100 dark:bg-red-900',
        border: 'border-red-200 dark:border-red-800',
      }
    case 'high':
      return {
        text: 'text-orange-800 dark:text-orange-200',
        bg: 'bg-orange-100 dark:bg-orange-900',
        border: 'border-orange-200 dark:border-orange-800',
      }
    case 'medium':
      return {
        text: 'text-yellow-800 dark:text-yellow-200',
        bg: 'bg-yellow-100 dark:bg-yellow-900',
        border: 'border-yellow-200 dark:border-yellow-800',
      }
    case 'low':
      return {
        text: 'text-blue-800 dark:text-blue-200',
        bg: 'bg-blue-100 dark:bg-blue-900',
        border: 'border-blue-200 dark:border-blue-800',
      }
    case 'none':
    default:
      return {
        text: 'text-muted-foreground',
        bg: 'bg-muted',
        border: 'border-border',
      }
  }
}

/**
 * Check if priority is high (high or urgent)
 * 
 * @param priority - Numeric priority value
 * @returns True if priority is high or urgent
 */
export function isHighPriority(priority: number): boolean {
  return priority >= 3
}

/**
 * Check if priority is urgent
 * 
 * @param priority - Numeric priority value
 * @returns True if priority is urgent
 */
export function isUrgentPriority(priority: number): boolean {
  return priority >= 4
}